/**
 * Singleton WebSocket connection to the pro-api /api/ws gateway.
 *
 * All hook instances share one connection. Topics are managed per-handler
 * so multiple components can subscribe/unsubscribe independently.
 *
 * Usage:
 *   const { subscribe, unsubscribe, isConnected } = useWebSocket();
 *   useEffect(() => {
 *     const handler = (msg) => { ... };
 *     subscribe(['entity:products:uuid'], handler);
 *     return () => unsubscribe(['entity:products:uuid'], handler);
 *   }, [subscribe, unsubscribe]);
 */
import * as React from 'react';
import { useAuth } from './auth/hooks';
import { handleSessionExpired } from './auth/session-expired';

/** Close code pro-api's /api/ws gateway uses when it force-closes a connection because the session backing it was invalidated (logout/revoke/disable) — see lion-energy-pro-api/src/index.ts. Distinct from any other close code so this can be told apart from a network blip. */
const SESSION_INVALIDATED_WS_CLOSE_CODE = 4401;

// Build the WebSocket base URL from the REST API URL env var
const API_BASE = (import.meta.env.VITE_API_URL as string) || 'http://localhost:3003/api';
const WS_BASE = API_BASE.replace(/^http/, 'ws').replace(/\/api$/, '');

export type WebSocketConnectionStatus =
  | 'idle'
  | 'connecting'
  | 'connected'
  | 'disconnected';

interface WsMessage {
  type: string;
  channel?: string;
  payload?: unknown;
  [key: string]: unknown;
}

type MessageHandler = (msg: WsMessage) => void;

// ── Module-level singleton ────────────────────────────────────────────────────
let socket: WebSocket | null = null;
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
let pingInterval: ReturnType<typeof setInterval> | null = null;
let reconnectDelay = 1000;
let activeToken: string | null = null;
let connectionStatus: WebSocketConnectionStatus = 'idle';

// channel → Set of handlers (including wildcard channels like 'entity:products:*')
const channelHandlers = new Map<string, Set<MessageHandler>>();
// topics that have been sent to the server
const serverTopics = new Set<string>();
// listeners notified when connection state changes
const stateListeners = new Set<() => void>();

function notifyState() { stateListeners.forEach((fn) => fn()); }

function setConnectionStatus(next: WebSocketConnectionStatus) {
  if (connectionStatus === next) return;
  connectionStatus = next;
  notifyState();
}

function dispatchMessage(msg: WsMessage) {
  if (msg.channel) {
    channelHandlers.get(msg.channel)?.forEach((h) => h(msg));
    // wildcard: entity:products:* matches entity:products:some-uuid
    const parts = (msg.channel as string).split(':');
    if (parts.length >= 2) {
      const wildcard = parts[0] + ':' + parts[1] + ':*';
      channelHandlers.get(wildcard)?.forEach((h) => h(msg));
    }
  }
  // always dispatch to type-based listeners
  channelHandlers.get('*')?.forEach((h) => h(msg));
}

function flushSubscriptions() {
  if (!socket || socket.readyState !== WebSocket.OPEN) return;
  if (serverTopics.size > 0) {
    socket.send(JSON.stringify({ type: 'subscribe', topics: [...serverTopics] }));
  }
}

function connectWs(token: string) {
  if (socket && (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING)) {
    return;
  }
  activeToken = token;
  setConnectionStatus('connecting');

  // Browser may still log a failed handshake natively; we intentionally avoid
  // console.error here so reconnect loops stay quiet in the app console.
  try {
    socket = new WebSocket(`${WS_BASE}/api/ws?token=${encodeURIComponent(token)}`);
  } catch {
    setConnectionStatus('disconnected');
    scheduleReconnect();
    return;
  }

  socket.onopen = () => {
    reconnectDelay = 1000;
    setConnectionStatus('connected');
    flushSubscriptions();
    // Ping every 25 s to keep the connection alive through ALB idle timeouts (60 s
    // default) and to keep the server-side Redis subscriber's TCP socket active.
    if (pingInterval) clearInterval(pingInterval);
    pingInterval = setInterval(() => {
      if (socket?.readyState === WebSocket.OPEN) {
        socket.send(JSON.stringify({ type: 'ping' }));
      }
    }, 25_000);
  };

  socket.onmessage = (e) => {
    try {
      const msg: WsMessage = JSON.parse(e.data as string);
      dispatchMessage(msg);
    } catch { /* ignore malformed frames */ }
  };

  socket.onclose = (event) => {
    if (pingInterval) { clearInterval(pingInterval); pingInterval = null; }
    socket = null;

    if (event.code === SESSION_INVALIDATED_WS_CLOSE_CODE) {
      // Terminal — the token backing this connection was just invalidated
      // server-side. Do not reconnect with it; react the same as a 401.
      activeToken = null;
      setConnectionStatus('idle');
      notifyState();
      handleSessionExpired();
      return;
    }

    if (activeToken) {
      setConnectionStatus('disconnected');
      scheduleReconnect();
    } else {
      setConnectionStatus('idle');
      notifyState();
    }
  };

  // onerror always precedes onclose for failed handshakes — close to unify
  // reconnect handling without logging (the browser already surfaces the failure).
  socket.onerror = () => {
    try {
      socket?.close();
    } catch { /* ignore */ }
  };
}

function scheduleReconnect() {
  if (!activeToken || reconnectTimer) return;
  reconnectTimer = setTimeout(() => {
    reconnectTimer = null;
    reconnectDelay = Math.min(reconnectDelay * 2, 30_000);
    if (activeToken) connectWs(activeToken);
  }, reconnectDelay);
}

function disconnectWs() {
  activeToken = null;
  if (reconnectTimer) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
  if (pingInterval) {
    clearInterval(pingInterval);
    pingInterval = null;
  }
  if (socket) {
    try {
      socket.close();
    } catch { /* ignore */ }
    socket = null;
  }
  setConnectionStatus('idle');
}

// ── Hook ──────────────────────────────────────────────────────────────────────

export function useWebSocket() {
  const { accessToken } = useAuth();
  const token = accessToken ?? localStorage.getItem('access_token');

  const [isConnected, setIsConnected] = React.useState(
    () => socket?.readyState === WebSocket.OPEN,
  );
  const [status, setStatus] = React.useState<WebSocketConnectionStatus>(
    () => connectionStatus,
  );

  React.useEffect(() => {
    const listener = () => {
      setIsConnected(socket?.readyState === WebSocket.OPEN);
      setStatus(connectionStatus);
    };
    stateListeners.add(listener);
    listener();
    return () => { stateListeners.delete(listener); };
  }, []);

  React.useEffect(() => {
    if (token) {
      connectWs(token);
      return;
    }
    disconnectWs();
  }, [token]);

  const subscribe = React.useCallback((topics: string[], handler: MessageHandler) => {
    topics.forEach((topic) => {
      if (!channelHandlers.has(topic)) channelHandlers.set(topic, new Set());
      channelHandlers.get(topic)!.add(handler);
      if (!serverTopics.has(topic)) {
        serverTopics.add(topic);
        if (socket?.readyState === WebSocket.OPEN) {
          socket.send(JSON.stringify({ type: 'subscribe', topics: [topic] }));
        }
      }
    });
  }, []);

  const unsubscribe = React.useCallback((topics: string[], handler?: MessageHandler) => {
    topics.forEach((topic) => {
      if (handler) {
        channelHandlers.get(topic)?.delete(handler);
        if (channelHandlers.get(topic)?.size === 0) channelHandlers.delete(topic);
      } else {
        channelHandlers.delete(topic);
      }
      // Only tell the server to unsubscribe if no handlers remain for this exact topic
      if (!channelHandlers.has(topic) && serverTopics.has(topic)) {
        serverTopics.delete(topic);
        if (socket?.readyState === WebSocket.OPEN) {
          socket.send(JSON.stringify({ type: 'unsubscribe', topics: [topic] }));
        }
      }
    });
  }, []);

  return { isConnected, status, subscribe, unsubscribe };
}
